import MaterialIcon from '@/components/atom/MaterialIcon';
import { AdditionalOrderProductModel } from '@/entities/additional-order/model/AdditionalOrder';
import theme from '@/styles/theme';
import { Button } from 'doctornow-design-system';
import { useEffect, useRef, useState } from 'react';
import { MAX_ADD_PRODUCT_QUANTITY } from '../hooks/AdditionalOrderViewModel';

interface Props {
  product: AdditionalOrderProductModel;
  isSelected: boolean;
  selectedCount: number;
  onAddProductToCart: (product: AdditionalOrderProductModel) => void;
  onRemoveProductFromCart: (product: AdditionalOrderProductModel) => void;
}

const AdditionalOrderProductItem = (props: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  const hasDescription = props.product.description.trim().length > 0;

  useEffect(() => {
    if (!hasDescription || !descriptionRef.current) {
      setHasOverflow(false);
      return;
    }

    const checkOverflow = () => {
      const element = descriptionRef.current;
      if (element) {
        const isOverflowing = element.scrollHeight > element.clientHeight;

        setHasOverflow(isOverflowing);
      }
    };

    checkOverflow();

    // 윈도우 리사이즈 시에도 체크
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [hasDescription, props.product.description]);

  const toggleDescription = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className='flex items-center gap-[16px] p-[12px_24px]'>
      <div className='flex h-[80px] w-[80px] shrink-0 items-center justify-center overflow-hidden rounded-[7px] bg-gray-30'>
        {props.product.imageUrl ? (
          <img src={props.product.imageUrl} alt={props.product.name} className='h-full w-full object-cover' />
        ) : (
          <MaterialIcon name='healing' size={36} color={theme.color.gray200} filled />
        )}
      </div>

      <div className='flex min-w-0 flex-1 flex-col gap-[8px] py-[4px]'>
        <div className='flex flex-col gap-[2px]'>
          <span className='typography-ui-15m text-gray-700'>{props.product.name}</span>

          {hasDescription && (
            <div className='flex flex-col'>
              <p
                ref={descriptionRef}
                className={`typography-ui-12r text-gray-400 ${!isExpanded ? 'line-clamp-2' : ''}`}
              >
                {props.product.description}
              </p>
              {hasOverflow && !isExpanded && (
                <span className='typography-ui-12r cursor-pointer text-gray-400 underline' onClick={toggleDescription}>
                  더보기
                </span>
              )}
            </div>
          )}
        </div>
        <span className='typography-ui-15sb text-gray-800'>{props.product.getDisplayPrice()}</span>
      </div>

      {props.isSelected ? (
        <div className='flex h-[32px] w-[72px] shrink-0 items-center justify-center rounded-[8px] border border-primary-600 border-solid'>
          <div
            className='flex h-full items-center px-[6px] py-[8px]'
            onClick={() => props.onRemoveProductFromCart(props.product)}
          >
            <MaterialIcon name='remove' size={16} color={theme.color.gray700} />
          </div>
          <span className='typography-ui-15sb w-[24px] text-center text-gray-700'>{props.selectedCount}</span>
          <div
            className='flex h-full items-center px-[6px] py-[8px]'
            onClick={() => props.onAddProductToCart(props.product)}
          >
            <MaterialIcon
              name='add'
              size={16}
              color={props.selectedCount === MAX_ADD_PRODUCT_QUANTITY ? theme.color.gray100 : theme.color.gray700}
            />
          </div>
        </div>
      ) : (
        <Button
          size='s'
          buttonColor='gray'
          buttonStyle='contained'
          onClick={() => props.onAddProductToCart(props.product)}
          className='h-[32px] w-[72px] shrink-0'
        >
          담기
        </Button>
      )}
    </div>
  );
};

export default AdditionalOrderProductItem;
